import { Signal } from '@angular/core';
import { StringUuid } from '../../domain/model/uuid/string-uuid';
import { StorePen } from '../../storage/data-store/model/master-data/store-pen';
import { StoreGroup } from '../../storage/data-store/model/master-data/store-group';
import { StoreFeed } from '../../storage/data-store/model/master-data/store-feed';
import { StoreBatchWeighing } from '../../storage/data-store/model/registrations/store-batch-weighing';
import { StoreTrial } from '../../storage/data-store/model/master-data/store-trial';
import { StoreSection } from '../../storage/data-store/model/master-data/store-section';
import { StoreTrialPhase } from '../../storage/data-store/model/master-data/store-trial-phase';
import { StoreBatch } from '../../storage/data-store/model/master-data/store-batch';
import { StoreApprovalStatus } from '../../storage/data-store/model/registrations/store-approval-status';
import { WeighingType } from '../../domain/model/weighing-type';
import { StoreTrialPhaseType } from '../../storage/data-store/model/master-data/store-trial-phase-type';
import { UserInfo } from '../../domain/model/user-info';
import { PigGender } from '../../domain/model/pig-gender.type';
import { Uuid } from '../../domain/model/uuid/uuid.type';
import { StoreAnimalWeighing } from '../../storage/data-store/model/registrations/store-animal-weighing';
import { BatchWeighingDomainService } from '../../domain/services/batch-weighings/batch-weighing.domain.service';
import { SectionOverviewViewModel } from '../../viewmodels/section-overview/section-overview.viewmodel';
import { getBatchWeighingsForSection, getBatchWeighingsWithType } from './data-store-batch-weighing.function';
import { ApprovalStatusViewModel } from '../../viewmodels/shared/approval-status.viewmodel';
import { SectionOverviewBatchRowViewModel } from '../../viewmodels/section-overview/section-overview-batch-row.viewmodel';
import { BatchWeighingViewModel } from '../../viewmodels/shared/batch-weighing.viewmodel';
import { BatchViewModel } from '../../viewmodels/shared/batch.viewmodel';
import { FeedViewModel } from '../../viewmodels/shared/feed.viewmodel';
import { GroupViewModel } from '../../viewmodels/shared/group.viewmodel';
import { PenViewModel } from '../../viewmodels/shared/pen.viewmodel';
import { SectionViewModel } from '../../viewmodels/shared/section.viewmodel';
import { TrialViewModel } from '../../viewmodels/shared/trial.viewmodel';
import { UpdateInfoViewModel } from '../../viewmodels/shared/update.viewmodel';
import { UserInfoViewModel } from '../../viewmodels/shared/user-info.viewmodel';
import { WeighingStageInfoViewModel } from '../../viewmodels/shared/weighing-stage-info.viewmodel';
import { WeighingStageStatusViewModel } from '../../viewmodels/shared/weighing-stage-status.viewmodel';
import { WeighingStageTypeViewModel } from '../../viewmodels/shared/weighing-stage-type.viewmodel';
import { getById, getByIds } from '../record.function';
import { SectionOverviewBatchRowWeighingViewModel } from '../../viewmodels/section-overview/section-overview-batch-row-weighing.viewmodel';
import { WeighingTypeEnum } from '../../domain/model/model-enums';
import { PigGenderViewModel } from '../../viewmodels/shared/pig-gender.viewmodel';
import { PigGenderTypes } from '../../domain/model/pig-gender-types';
import { SectionOverviewTrialPhasesViewModel } from '../../viewmodels/section-overview/section-overview-trial-phases.viewmodel';
import { StoreUpdateInfo } from '../../storage/store-update-info';

export function mapToPenViewModel(pen: StorePen): PenViewModel {
  return new PenViewModel(new StringUuid(pen.id), pen.name, pen.number);
}

export function mapToGroupViewModel(group: StoreGroup): GroupViewModel {
  return new GroupViewModel(new StringUuid(group.id), group.name, group.number);
}

export function mapToFeedViewModel(feed: StoreFeed): FeedViewModel {
  return new FeedViewModel(new StringUuid(feed.id), feed.name, feed.number);
}

// export function mapToTrialSectionRows(
//   trial: StoreTrial,
//   section: StoreSection,
//   pens: StorePen[],
//   bwsForSectionAndTrial: StoreBatchWeighing[]
// ) : TrialSectionRowViewModel{
//   return new TrialSectionRowViewModel(
//     mapToTrialViewModel(trial),
//     mapToSectionViewModel(section),
//     mapToPenViewModels(pens),
//     mapToWeighingStageInfoViewModels(trial.phases, bwsForSectionAndTrial)
//   );
// }

export function mapToWeighingStageInfoViewModels(
  trialsPhases: StoreTrialPhase[],
  batchWeighings: StoreBatchWeighing[]
): WeighingStageInfoViewModel[] {
  const stageInfoVMs = trialsPhases.map((trialPhase) => {
    return mapToWeighingStageInfoViewModel(
      trialPhase,
      getBatchWeighingsWithType(mapStoreTrialPhaseTypeToWeighingType(trialPhase.type), batchWeighings)
    );
  });

  return stageInfoVMs;
}

export function mapToWeighingStageInfoViewModel(
  trialPhase: StoreTrialPhase,
  batchWeighing: StoreBatchWeighing[]): WeighingStageInfoViewModel {
  const approvals = batchWeighing.flatMap((bw) => {
    return mapApprovalStatusViewModels(bw.approvals);
  });

  const stageInfo = new WeighingStageInfoViewModel(
    new StringUuid(trialPhase.id),
    trialPhase.number,
    new WeighingStageTypeViewModel(trialPhase.type.name),
    trialPhase.expectedStartDate,
    trialPhase.expectedEndDate,
    trialPhase.expectedStartWeightKg,
    trialPhase.expectedEndWeightKg,
    new WeighingStageStatusViewModel(approvals)
  );
  return stageInfo;
}

export function mapToBatchViewModels(batches: StoreBatch[]): BatchViewModel[] {
  return batches.map((batch) => {
    return new BatchViewModel(new StringUuid(batch.id), batch.name);
  });
}

export function mapApprovalStatusViewModels(batchApprovals: StoreApprovalStatus[]): ApprovalStatusViewModel[] {
  const status = batchApprovals.map((approval) => {
    return new ApprovalStatusViewModel(new StringUuid(approval.id), approval.date, approval.approvedBy, approval.status);
  });
  return status;
}

export function mapToBatchViewModel(batch: StoreBatch): BatchViewModel {
  return new BatchViewModel(new StringUuid(batch.id), batch.name);
}

export function mapToPenViewModels(pens: StorePen[]): PenViewModel[] {
  return pens.map((pen) => {
    return new PenViewModel(new StringUuid(pen.id), pen.name, pen.number);
  });
}

export function mapStoreTrialPhaseTypeToWeighingType(phaseType: StoreTrialPhaseType) {
  return new WeighingType(phaseType.name);
}

export function mapToUserInfoViewModel(user: UserInfo): UserInfoViewModel {
  return new UserInfoViewModel(user.userId, user.name);
}

export function mapToUpdateInfoViewModel(updateInfo: StoreUpdateInfo) {
  return new UpdateInfoViewModel(
    updateInfo.lastServerUpdate,
    updateInfo.lastLocalUpdate,
    updateInfo.lastEditedByUser !== null ?
      mapToUserInfoViewModel(updateInfo.lastEditedByUser) :
      null
  )
}

export function mapToBatchWeighingViewModel(
  id: string,
  batchId: string,
  sectionId: string,
  trialId: string,
  batchNumber: number,
  batchName: string,
  weighingType: WeighingType,
  pigGender: PigGender,
  update: UpdateInfoViewModel,
  animalWeighingIds: Uuid[],
  adjacentBws: {
    previousBwId: string | undefined;
    nextBwId: string | undefined;
  }
): BatchWeighingViewModel {
  return new BatchWeighingViewModel(
    new StringUuid(id),
    animalWeighingIds,
    new StringUuid(batchId),
    new StringUuid(sectionId),
    new StringUuid(trialId),
    batchNumber,
    batchName,
    weighingType,
    mapToPigGenderViewModel(pigGender),
    update,
    adjacentBws
  );
}

export function mapToPigGenderViewModel(pigGender: PigGender): PigGenderViewModel {
  return new PigGenderViewModel(pigGender.id, pigGender.name);
}

export function mapToTrialViewModel(trial: StoreTrial): TrialViewModel {
  return new TrialViewModel(new StringUuid(trial.id), trial.name);
}

export function mapToSectionViewModel(section: StoreSection): SectionViewModel {
  return new SectionViewModel(new StringUuid(section.id), section.name);
}

export function mapApprovalStatusViewModel(
  approvalStatus: StoreApprovalStatus | undefined): ApprovalStatusViewModel | null {
  if (!approvalStatus) {
    return null;
  }
  return new ApprovalStatusViewModel(
        new StringUuid(approvalStatus.id),
        approvalStatus.date,
        mapToUserInfoViewModel(approvalStatus.approvedBy),
        approvalStatus.status);
  }

export function mapTrialToSectionOverviewViewModels(
  sections: Signal<Record<string, StoreSection>>,
  trial: StoreTrial,
  batches: Signal<Record<string, StoreBatch>>,
  batchWeighings: StoreBatchWeighing[],
  animalWeighings: Signal<Record<string, StoreAnimalWeighing>>
): SectionOverviewViewModel[] {
  const retViewModels: SectionOverviewViewModel[] = [];
  for (const key in sections()) {
    const section = sections()[key];
    if (section === undefined) {
      throw new Error(`mapTrialToSectionWeighingVMs: Section with id ${key} not found`);
    }
    const bwsForSection = getBatchWeighingsForSection(section.id.toString(), batchWeighings);
    const sectionViewModel = mapToSectionViewModel(section);
    retViewModels.push(
      new SectionOverviewViewModel(
        new StringUuid(trial.id),
        mapToSectionOverviewTrialPhasesViewModel(trial),
        sectionViewModel,
        mapAllToSectionOverviewBatchRowViewModel(
          new StringUuid(trial.id),
          new StringUuid(section.id),
          batches,
          bwsForSection,
          animalWeighings
        )
      )
    );
  }

  return retViewModels;
}

export function mapToSectionOverviewTrialPhasesViewModel(
  trial: StoreTrial

): SectionOverviewTrialPhasesViewModel[] {
  return trial.phases.map((phase) => {
    return new SectionOverviewTrialPhasesViewModel(
      new StringUuid(phase.id),
      new StringUuid(trial.id),
      phase.number,
      (new StoreTrialPhaseType(phase.type.name)).asWeighingType(),
      phase.expectedStartDate,
      phase.expectedEndDate,
      phase.expectedStartWeightKg,
      phase.expectedEndWeightKg
    );
  });
}


export function mapAllToSectionOverviewBatchRowViewModel(
  trialId: Uuid,
  sectionId: Uuid,
  batches: Signal<Record<string, StoreBatch>>,
  batchWeighings: StoreBatchWeighing[],
  animalWeighings: Signal<Record<string, StoreAnimalWeighing>>
): SectionOverviewBatchRowViewModel[] {
  if (batchWeighings.length === 0) {
    return [];
  }
  const sectionBatchWeighings = getBatchWeighingsForSection(sectionId.asString(), batchWeighings);
  const batchIds = [...new Set(sectionBatchWeighings.map((bw) => bw.batchId))];

  if (batchIds.length === 0) {
    return [];
  }
  return batchIds.map((id) => {
    const batch = getById(batches, id);
    if (!batch) {
      throw new Error(`Batch with id ${id} not found in batches`);
    }

    const sectionBatchWeighingsForBatch = sectionBatchWeighings.filter((bw) => bw.batchId === id);
    const initial = sectionBatchWeighingsForBatch.find((bw) => bw.weighingType.type === WeighingTypeEnum.Initial);
    const final = sectionBatchWeighingsForBatch.find((bw) => bw.weighingType.type === WeighingTypeEnum.Final);
    const interim = sectionBatchWeighingsForBatch.filter((bw => bw.weighingType.type === WeighingTypeEnum.Interim));
    const batchRowWeighings: SectionOverviewBatchRowWeighingViewModel[] = [];

    const initialWeighingViewModel = initial ?
      mapToSectionOverviewBatchRowWeighingViewModels(
      batch,
      initial,
      getByIds(animalWeighings, initial.animalWeighingIds)) :
      null;
    if (initialWeighingViewModel) {
      batchRowWeighings.push(initialWeighingViewModel);
    }

    interim.map((iw) => {
      batchRowWeighings.push(
        mapToSectionOverviewBatchRowWeighingViewModels(
          batch,
          iw,
          getByIds(animalWeighings, iw.animalWeighingIds)
        )
      );
    });

    const finalWeighingViewModel = final ?
      mapToSectionOverviewBatchRowWeighingViewModels(
      batch,
      final,
      getByIds(animalWeighings, final.animalWeighingIds)) :
      null;
      if( finalWeighingViewModel) {
        batchRowWeighings.push(finalWeighingViewModel);
      }

    const pigGenderViewModel = initial ?
        mapToPigGenderViewModel(initial.gender) :
        mapToPigGenderViewModel(PigGenderTypes.Sow);

    return new SectionOverviewBatchRowViewModel(
      sectionId,
      trialId,
      new StringUuid(batch.id),
      batch.number,
      pigGenderViewModel,
      batchRowWeighings);
  });
}

export function mapToSectionOverviewBatchRowWeighingViewModels(
  batch: StoreBatch,
  batchWeighing: StoreBatchWeighing,
  animalWeighings: StoreAnimalWeighing[]
): SectionOverviewBatchRowWeighingViewModel {
  const animals: number[] = [];
  const weights: number[] = [];

  animalWeighings.forEach((aw) => {
    animals.push(aw.inputNumber);
    weights.push(aw.inputWeight);
  });
  const averageWeight = BatchWeighingDomainService.averageWeightRounded(animals, weights);
  const latestApproval = batchWeighing.approvals.length > 0 ?
     batchWeighing.approvals[batchWeighing.approvals.length - 1] :
     null;
  const lastestApprovalViewModel = latestApproval !== null ?
     mapApprovalStatusViewModel(latestApproval) :
     null;
  const date = (latestApproval !== null && latestApproval !== undefined) ?
    latestApproval.date :
    batchWeighing.updateInfo.lastLocalUpdate;

  return new SectionOverviewBatchRowWeighingViewModel(
    new StringUuid(batchWeighing.id),
    batch.number,
    batchWeighing.weighingType,
    averageWeight,
    date,
    batchWeighing.sequenceNumber,
    lastestApprovalViewModel);
}
